import React, { useState } from 'react';
import { FaChevronRight, FaChartBar, FaChartLine, FaEye } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import KeyboardNavigableSelect from '@/components/ui/KeyboardNavigableSelect';
import { useASINSalesHistory } from '@/context/ASINSalesHistoryContext';

export type NavigationSection = 'asin-sales-history' | 'overall-sales-history' | 'asin-viewer';

interface NavigationMenuProps {
  activeSection: NavigationSection;
  onSectionChange: (section: NavigationSection) => void;
}

const NavigationMenu: React.FC<NavigationMenuProps> = ({
  activeSection,
  onSectionChange
}) => {
  const [expandedSection, setExpandedSection] = useState<NavigationSection>('asin-sales-history');
  
  const {
    companies,
    selectedCompany,
    availableASINs,
    isLoadingCompanies,
    isLoadingAsins,
    setSelectedCompany,
    setSelectedFilter,
    selectedFilter
  } = useASINSalesHistory();

  // Local state for view mode toggle
  const [viewMode, setViewMode] = useState<'daily' | 'cumulative'>('daily');

  const handleSectionClick = (section: NavigationSection) => {
    if (expandedSection === section) {
      // If clicking the already expanded section, don't collapse it
      onSectionChange(section);
    } else {
      // Expand new section and switch to it
      setExpandedSection(section);
      onSectionChange(section);
    }
  };

  const navigationItems = [
    {
      id: 'asin-sales-history' as NavigationSection,
      title: 'ASIN Sales History',
      icon: FaChartBar,
      disabled: false,
      hasSubmenu: true,
    },
    {
      id: 'overall-sales-history' as NavigationSection,
      title: 'Overall Sales History',
      icon: FaChartLine,
      disabled: false,
      hasSubmenu: true,
    },
    {
      id: 'asin-viewer' as NavigationSection,
      title: 'ASIN Viewer',
      icon: FaEye,
      disabled: false,
      hasSubmenu: true,
    },
  ];

  const companyOptions = companies.map(company => ({
    value: company.id,
    label: company.client
  }));

  const asinOptions = availableASINs.map(asin => ({ 
    value: asin, 
    label: asin 
  }));

  return (
    <nav className="flex-1 mt-4 overflow-y-auto">
      <div className="space-y-1">
        {navigationItems.map((item) => {
          const isExpanded = expandedSection === item.id;
          const isActive = activeSection === item.id;
          const Icon = item.icon;

          return (
            <div key={item.id} className="space-y-0">
              {/* Main Navigation Button */}
              <button
                onClick={() => handleSectionClick(item.id)}
                disabled={item.disabled}
                className={`
                  w-full flex items-center justify-between px-4 py-3 text-left text-sm font-medium
                  transition-all duration-200 ease-in-out
                  ${isActive 
                    ? 'bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 border-r-2 border-violet-500' 
                    : 'text-gray-700 dark:text-gray-300'
                  }
                  ${item.disabled 
                    ? 'opacity-50 cursor-not-allowed' 
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer'
                  }
                `}
              >
                <div className="flex items-center">
                  <Icon className={`mr-3 h-5 w-5 ${isActive ? 'text-violet-600 dark:text-violet-400' : ''}`} />
                  <span>{item.title}</span>
                </div>
                
                {item.hasSubmenu && !item.disabled && (
                  <motion.div
                    animate={{ rotate: isExpanded ? 90 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <FaChevronRight className="h-4 w-4" />
                  </motion.div>
                )}
              </button>

              {/* Submenu Content */}
              <AnimatePresence>
                {isExpanded && item.hasSubmenu && !item.disabled && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    className="overflow-hidden bg-gray-50 dark:bg-gray-800/50"
                  >
                    <div className="px-6 py-4 space-y-4">
                      {/* ASIN Sales History Submenu */}
                      {item.id === 'asin-sales-history' && (
                        <>
                          <div>
                            <label className="block text-xs font-medium mb-2 text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                              Select Company
                            </label>
                            <KeyboardNavigableSelect
                              options={companyOptions}
                              value={selectedCompany || ''}
                              onValueChange={setSelectedCompany}
                              placeholder={isLoadingCompanies ? "Loading..." : "Select a company"}
                              className="w-full"
                              triggerClassName="w-full text-gray-900 dark:text-white bg-white dark:bg-gray-700 text-sm"
                              disabled={isLoadingCompanies}
                              contentStyle={{ backgroundColor: 'white', color: 'black', zIndex: 50 }}
                              itemStyle={{ color: 'black' }}
                            />
                          </div>
                          
                          <div>
                            <label className="block text-xs font-medium mb-2 text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                              Filter by ASIN
                            </label>
                            <KeyboardNavigableSelect
                              options={[{ value: 'all', label: 'All ASINs' }, ...asinOptions]}
                              value={selectedFilter?.asin || 'all'}
                              onValueChange={(asin) => {
                                if (asin === 'all') {
                                  setSelectedFilter(selectedFilter ? { ...selectedFilter, asin: undefined } : null);
                                } else {
                                  setSelectedFilter({ ...selectedFilter, asin });
                                }
                              }}
                              placeholder={!selectedCompany ? "Select a company first" : isLoadingAsins ? "Loading ASINs..." : "All ASINs"}
                              className="w-full"
                              triggerClassName="w-full text-gray-900 dark:text-white bg-white dark:bg-gray-700 text-sm"
                              disabled={!selectedCompany || isLoadingAsins}
                              contentStyle={{ backgroundColor: 'white', color: 'black', zIndex: 50 }}
                              itemStyle={{ color: 'black' }}
                            />
                          </div>
                        </>
                      )}

                      {/* Overall Sales History Submenu */}
                      {item.id === 'overall-sales-history' && (
                        <>
                          <div>
                            <label className="block text-xs font-medium mb-2 text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                              Select Company
                            </label>
                            <KeyboardNavigableSelect
                              options={companyOptions}
                              value={selectedCompany || ''}
                              onValueChange={setSelectedCompany}
                              placeholder={isLoadingCompanies ? "Loading..." : "Select a company"}
                              className="w-full"
                              triggerClassName="w-full text-gray-900 dark:text-white bg-white dark:bg-gray-700 text-sm"
                              disabled={isLoadingCompanies}
                              contentStyle={{ backgroundColor: 'white', color: 'black', zIndex: 50 }}
                              itemStyle={{ color: 'black' }}
                            />
                          </div>
                          
                          <div>
                            <label className="block text-xs font-medium mb-2 text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                              View Mode
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                              <button
                                onClick={() => {
                                  console.log('[NavigationMenu] Daily Breakdown clicked');
                                  setViewMode('daily');
                                  if ((window as any).setOverallViewMode) {
                                    (window as any).setOverallViewMode('daily');
                                  }
                                }}
                                className={`px-3 py-2 text-xs rounded transition-colors ${
                                  viewMode === 'daily'
                                    ? 'bg-violet-600 text-white hover:bg-violet-700'
                                    : 'bg-violet-100 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 hover:bg-violet-200 dark:hover:bg-violet-900/40'
                                }`}
                              >
                                Daily Breakdown
                              </button>
                              <button
                                onClick={() => {
                                  console.log('[NavigationMenu] Cumulative Totals clicked');
                                  setViewMode('cumulative');
                                  if ((window as any).setOverallViewMode) {
                                    (window as any).setOverallViewMode('cumulative');
                                  }
                                }}
                                className={`px-3 py-2 text-xs rounded transition-colors ${
                                  viewMode === 'cumulative'
                                    ? 'bg-violet-600 text-white hover:bg-violet-700'
                                    : 'bg-violet-100 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 hover:bg-violet-200 dark:hover:bg-violet-900/40'
                                }`}
                              >
                                Cumulative Totals
                              </button>
                            </div>
                          </div>
                        </>
                      )}

                      {/* ASIN Viewer Submenu */}
                      {item.id === 'asin-viewer' && (
                        <>
                          <div>
                            <label className="block text-xs font-medium mb-2 text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                              Select Company
                            </label>
                            <KeyboardNavigableSelect
                              options={companyOptions}
                              value={selectedCompany || ''}
                              onValueChange={setSelectedCompany}
                              placeholder={isLoadingCompanies ? "Loading..." : "Select a company"}
                              className="w-full"
                              triggerClassName="w-full text-gray-900 dark:text-white bg-white dark:bg-gray-700 text-sm"
                              disabled={isLoadingCompanies}
                              contentStyle={{ backgroundColor: 'white', color: 'black', zIndex: 50 }}
                              itemStyle={{ color: 'black' }}
                            />
                          </div>
                          
                          <div>
                            <label className="block text-xs font-medium mb-2 text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                              Filter by ASIN
                            </label>
                            <KeyboardNavigableSelect
                              options={[{ value: 'all', label: 'All ASINs' }, ...asinOptions]}
                              value={selectedFilter?.asin || 'all'}
                              onValueChange={(asin) => {
                                if (asin === 'all') {
                                  setSelectedFilter(selectedFilter ? { ...selectedFilter, asin: undefined } : null);
                                } else {
                                  setSelectedFilter({ ...selectedFilter, asin });
                                }
                              }}
                              placeholder={!selectedCompany ? "Select a company first" : isLoadingAsins ? "Loading ASINs..." : "All ASINs"}
                              className="w-full"
                              triggerClassName="w-full text-gray-900 dark:text-white bg-white dark:bg-gray-700 text-sm"
                              disabled={!selectedCompany || isLoadingAsins}
                              contentStyle={{ backgroundColor: 'white', color: 'black', zIndex: 50 }}
                              itemStyle={{ color: 'black' }}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </nav>
  );
};

export default NavigationMenu;